import { useState } from 'react';
import { motion } from 'framer-motion';
import { LISTINGS } from './data';

// INVENTORY — full ledger, region tabs, and sortable code-based rows. NOT a 3-up grid.
export default function Inventory({ setPage }) {
  const regions = ['All', 'Hamptons', 'Aspen', 'Côte d\u2019Azur'];
  const [r, setR] = useState('All');
  const filter = (l) => {
    if (r === 'All') return true;
    if (r === 'Hamptons') return l.region.includes('NY');
    if (r === 'Aspen') return l.region.includes('CO');
    return l.region.includes('FR');
  };
  const list = LISTINGS.filter(filter);
  return (
    <div className="bg-[#F4EFE5] text-[#0E1B2D] min-h-screen">
      <section className="pt-32 pb-8 max-w-[1500px] mx-auto px-8 lg:px-14 border-b-2 border-[#1F3A5F]">
        <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#7B5527] mb-3">— Office II / Inventory ledger</p>
        <div className="grid lg:grid-cols-[1fr_auto] items-end gap-8">
          <h1 className="font-serif italic text-[44px] lg:text-[88px] leading-[0.95] text-[#1F3A5F]">11 estates currently on the books.</h1>
          <p className="font-serif text-base text-[#1F3A5F]/65 leading-[1.7] max-w-md justify-self-end">All listings are private. Asking prices are reviewed quarterly. Comp data shows the most recent comparable closing in the same micro-market.</p>
        </div>
      </section>

      {/* Region tabs */}
      <div className="border-b border-[#1F3A5F]/15 sticky top-20 bg-[#F4EFE5]/95 backdrop-blur-md z-30">
        <div className="max-w-[1500px] mx-auto px-8 lg:px-14 py-4 flex flex-wrap gap-2 font-mono text-[10px] tracking-[0.22em] uppercase">
          {regions.map(reg => (
            <button key={reg} onClick={() => setR(reg)} data-testid={`ml-region-${reg}`} className={`px-4 py-1.5 transition-colors ${r === reg ? 'bg-[#1F3A5F] text-[#F4EFE5]' : 'border border-[#1F3A5F]/25 text-[#1F3A5F]/65 hover:border-[#1F3A5F]'}`}>{reg}</button>
          ))}
        </div>
      </div>

      {/* Ledger rows — each is a horizontal index card with thumbnail */}
      <section className="max-w-[1500px] mx-auto px-8 lg:px-14 py-10">
        <div className="hidden lg:grid grid-cols-[80px_140px_1.6fr_120px_120px_120px_140px_120px] gap-5 pb-3 border-b border-[#1F3A5F]/30 mb-3 font-mono text-[9px] tracking-[0.3em] uppercase text-[#1F3A5F]/55">
          <span>Ref</span><span>Region</span><span>Estate</span><span>Sqft / Acres</span><span>Beds / Baths</span><span>Ask</span><span>Last comp</span><span></span>
        </div>
        <div className="space-y-3">
          {list.map((l, i) => (
            <motion.article key={l.ref} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }} className={`bg-white border ${l.status === 'sold' ? 'border-[#1F3A5F]/15 opacity-70' : l.status === 'reserved' ? 'border-[#7B5527]/40' : 'border-[#1F3A5F]/15 hover:border-[#1F3A5F]'} p-4 lg:p-5 grid grid-cols-2 lg:grid-cols-[80px_140px_1.6fr_120px_120px_120px_140px_120px] gap-x-5 gap-y-3 items-center transition-colors`}>
              <div className="lg:col-span-2 flex items-center gap-4">
                <div className="w-20 h-16 lg:w-24 lg:h-20 overflow-hidden border border-[#1F3A5F]/15 shrink-0">
                  <img src={l.img} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="lg:hidden">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#7B5527]">{l.ref}</p>
                  <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#1F3A5F]/50">{l.region}</p>
                </div>
              </div>
              <p className="hidden lg:block font-mono text-[10px] tracking-[0.3em] uppercase text-[#7B5527]">{l.ref}</p>
              <p className="hidden lg:block font-mono text-[9px] tracking-[0.25em] uppercase text-[#1F3A5F]/50">{l.region}</p>
              <div className="col-span-2 lg:col-span-1">
                <h3 className="font-serif italic text-xl text-[#1F3A5F]">{l.name}</h3>
                {l.status === 'reserved' && <span className="inline-block mt-1 font-mono text-[9px] tracking-[0.3em] uppercase text-[#7B5527] border border-[#7B5527]/40 px-2 py-0.5">Reserved</span>}
                {l.status === 'sold' && <span className="inline-block mt-1 font-mono text-[9px] tracking-[0.3em] uppercase text-[#1F3A5F]/40 border border-[#1F3A5F]/30 px-2 py-0.5">Sold ✓</span>}
              </div>
              <p className="font-mono text-xs text-[#1F3A5F]/70">{l.sqft} ft²<br /><span className="text-[#1F3A5F]/40">{l.acres} ac</span></p>
              <p className="font-mono text-xs text-[#1F3A5F]/70">{l.beds} BR<br /><span className="text-[#1F3A5F]/40">{l.baths} BA</span></p>
              <p className="font-serif text-lg text-[#7B5527]">{l.ask}</p>
              <p className="font-mono text-xs text-[#1F3A5F]/55">{l.comp}</p>
              <button disabled={l.status === 'sold'} onClick={() => setPage('viewing')} className="font-mono text-[10px] tracking-[0.3em] uppercase border border-[#1F3A5F]/30 px-3 py-2 hover:bg-[#1F3A5F] hover:text-[#F4EFE5] disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-testid={`ml-row-${l.ref}`}>View →</button>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
